import type { QueryClient } from '@tanstack/react-query'
import { messengerKeys } from '@/modules/messenger/hooks/useMessenger'
import { messengerService } from '@/modules/messenger/services/messenger.service'
import type { ChatMessage, CursorPage } from '@/modules/messenger/types/messenger.types'
import { useAuthStore } from '@/store/auth.store'

interface TypingPayload {
  chatId: string
  userId: string
  typing: boolean
}

interface RecordingPayload {
  chatId: string
  userId: string
  recording: boolean
}

type TypingListener = (payload: TypingPayload) => void
const typingListeners = new Set<TypingListener>()

type RecordingListener = (payload: RecordingPayload) => void
const recordingListeners = new Set<RecordingListener>()

/** Dedupe delivered-acks across WS reconnect / duplicate frames. */
const deliveredAckIds = new Set<string>()

/** Module-level, so it outlives any one component — must be dropped on
 * every session boundary (logout/login) or a re-login in the same tab
 * inherits the previous user's already-acked message ids and silently
 * skips acking them for the new session. See session-reset.ts. */
export function resetDeliveredAcks(): void {
  deliveredAckIds.clear()
}

/** `chat.typing` is ephemeral and never belongs in the query cache — a tiny
 * module-level pub-sub instead, subscribed to by whichever thread is
 * currently open (see useChatTyping). */
export function subscribeTyping(listener: TypingListener): () => void {
  typingListeners.add(listener)
  return () => typingListeners.delete(listener)
}

/** Same ephemeral pub-sub as subscribeTyping, for `chat.recording` — kept
 * separate so "typing a reply" and "recording a voice note" can be told
 * apart in the UI (see useChatRecordingListener). */
export function subscribeRecording(listener: RecordingListener): () => void {
  recordingListeners.add(listener)
  return () => recordingListeners.delete(listener)
}

/**
 * `chat.message_created` / `chat.member_added` arrive as full Notification
 * rows (`entityId` = chatId, nested `payload.message`). Ephemeral
 * `publishRealtime` events spread `{ type, chatId, ... }` onto the same
 * object the WS gateway puts in `envelope.payload`. Resolve chatId from
 * every shape so an open thread invalidates/patches — not only the inbox.
 */
function resolveChatId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined
  const p = payload as Record<string, unknown>
  if (typeof p.chatId === 'string' && p.chatId) return p.chatId
  if (typeof p.entityId === 'string' && p.entityId) return p.entityId
  const nested = p.payload
  if (nested && typeof nested === 'object') {
    const message = (nested as { message?: { chatId?: string } }).message
    if (typeof message?.chatId === 'string' && message.chatId) return message.chatId
  }
  return undefined
}

function extractLiveMessage(payload: unknown): ChatMessage | undefined {
  if (!payload || typeof payload !== 'object') return undefined
  const p = payload as Record<string, unknown>
  const nested = p.payload
  if (nested && typeof nested === 'object') {
    const message = (nested as { message?: ChatMessage }).message
    if (message && typeof message.id === 'string' && typeof message.chatId === 'string') return message
  }
  return undefined
}

function appendMessageToThreadCache(qc: QueryClient, chatId: string, message: ChatMessage) {
  qc.setQueryData(
    messengerKeys.messages(chatId),
    (old: { pages: CursorPage<ChatMessage>[]; pageParams: unknown[] } | undefined) => {
      if (!old?.pages?.length) return old
      const first = old.pages[0]
      if (!first) return old
      if (old.pages.some((page) => page.items.some((m) => m.id === message.id))) return old
      const pages = [...old.pages]
      pages[0] = { ...first, items: [...first.items, message], nextCursor: first.nextCursor ?? null }
      return { ...old, pages }
    },
  )
}

function patchMessageInThreadCache(qc: QueryClient, chatId: string, message: ChatMessage) {
  qc.setQueryData(
    messengerKeys.messages(chatId),
    (old: { pages: CursorPage<ChatMessage>[]; pageParams: unknown[] } | undefined) => {
      if (!old) return old
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          items: page.items.map((m) => (m.id === message.id ? { ...m, ...message } : m)),
        })),
      }
    },
  )
}

/** Payload may nest under Notification-shaped `{ payload: { message } }` or
 * arrive flat from `publishRealtime` as `{ chatId, message }`. */
function extractPatchedMessage(payload: unknown): ChatMessage | undefined {
  const live = extractLiveMessage(payload)
  if (live) return live
  if (!payload || typeof payload !== 'object') return undefined
  const message = (payload as { message?: ChatMessage }).message
  if (message && typeof message.id === 'string' && typeof message.chatId === 'string') return message
  return undefined
}

/** WhatsApp: delivered = reached recipient device. Ack as soon as this
 * browser gets `chat.message_created` over WS — even if another chat is
 * focused (inbox badge alone used to leave the sender on a single tick). */
function ackDeliveredIfNeeded(chatId: string, message: ChatMessage) {
  const me = useAuthStore.getState().session?.user?.id
  if (!me || message.authorId === me) return
  if (deliveredAckIds.has(message.id)) return
  deliveredAckIds.add(message.id)
  void messengerService.markDelivered(chatId, [message.id]).catch(() => {
    deliveredAckIds.delete(message.id)
  })
}

/**
 * Applies one live `chat.*` event. Dispatched straight off the WS
 * envelope's own `type` (not a Notification-shaped payload) — see
 * useNotificationRealtime.ts, which routes anything starting with `chat.`
 * here before falling through to the notification-shaped handling every
 * other event type still uses.
 */
export function applyChatRealtime(qc: QueryClient, type: string, payload: unknown) {
  if (type === 'chat.typing') {
    const p = payload as Partial<TypingPayload>
    if (!p?.chatId || !p.userId) return
    for (const listener of typingListeners) listener({ chatId: p.chatId, userId: p.userId, typing: Boolean(p.typing) })
    return
  }

  if (type === 'chat.recording') {
    const p = payload as Partial<RecordingPayload>
    if (!p?.chatId || !p.userId) return
    for (const listener of recordingListeners) {
      listener({ chatId: p.chatId, userId: p.userId, recording: Boolean(p.recording) })
    }
    return
  }

  const chatId = resolveChatId(payload)

  if (type === 'chat.message_created' && chatId) {
    const message = extractLiveMessage(payload)
    if (message) {
      appendMessageToThreadCache(qc, chatId, message)
      ackDeliveredIfNeeded(chatId, message)
    }
  }

  // Second step of voice send finished — flip voiceAvailable on the stub
  // peers already have so ChatVoiceNote re-fetches instead of staying on
  // "isn't available yet" until a hard refresh.
  if (type === 'chat.voice_ready' && chatId) {
    const message = extractPatchedMessage(payload)
    if (message) {
      patchMessageInThreadCache(qc, chatId, { ...message, voiceAvailable: true })
    }
  }

  void qc.invalidateQueries({ queryKey: messengerKeys.chats() })
  void qc.invalidateQueries({ queryKey: messengerKeys.unread() })
  if (chatId) {
    void qc.invalidateQueries({ queryKey: messengerKeys.messages(chatId) })
    void qc.invalidateQueries({ queryKey: messengerKeys.chatDetail(chatId) })
  } else {
    void qc.invalidateQueries({ queryKey: [...messengerKeys.all, 'messages'] })
  }
}
